import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SimState, TcSimType } from '../../types/tc-sim/tc-sim.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  activateTcSim,
  applySuggestedDataPlanType,
  bulkApplySuggestedDataPlanType,
  deactivateTcSim,
  deleteTcSimDetails,
  getTcSimDetails,
  getTcSimsList,
  patchTcSimContract,
  patchTcSimDetails,
  postSimVerizonActivationStatus,
  postTcSimDetails,
  postTcSims,
  postTcSimFixService,
  postTcSimOwnershipTransfer,
  restoreTcSim,
  suspendTcSim,
  refreshTcSimStatus,
  updateCarrierDataPlanType,
} from './tcSims.action';

export type TcSimsRouteParams = RouteParamsType & {
  model_id: string;
  sim_status: SimState | '';
  data_plan_type_id: string;
  service_type_id: string;
  is_connected: 'all' | 'true' | 'false';
  is_synchronized: 'all' | 'true' | 'false';
  country_category: unknown;
};

export interface TcSimsState {
  tcSimsList: null | PaginationType<TcSimType>;
  tcSimsListFetchStatus: FETCH_STATUS;
  simVerizonActivationStatusPostStatus: FETCH_STATUS;
  simVerizonActivation: null | { requestId: string };
  tcSimsRouteParams: TcSimsRouteParams;
  tcSimDetails: null | TcSimType;
  tcSimDetailsFetchStatus: FETCH_STATUS;
  tcSimDetailsPostStatus: FETCH_STATUS;
  tcSimDetailsPatchStatus: FETCH_STATUS;
  tcSimDetailsDeleteStatus: FETCH_STATUS;
  tcSimDetailsStatusPatchStatus: FETCH_STATUS;
  tcSimStatusRefreshStatus: FETCH_STATUS;
  tcSimApplySuggestedDataPlanTypeStatus: FETCH_STATUS;
  updateCarrierDataPlanStatus: FETCH_STATUS;
  tcSimsBulkApplySuggestedDataPlanTypeStatus: FETCH_STATUS;
  tcSimsPostStatus: FETCH_STATUS;
  tcSimFixServiceStatus: FETCH_STATUS;
  tcSimContractPatchStatus: FETCH_STATUS;
  tcSimOwnershipTransferStatus: FETCH_STATUS;
  tcSimsBulk: null | Array<unknown>;
}

export const initialTcSimsRouteParams: TcSimsRouteParams = {
  page: 1,
  limit: 10,
  order: '-tc_sim.id',
  q: '',
  in_service: 'all',
  model_id: '',
  sim_status: '',
  country_id: '',
  country_category: '',
  data_plan_type_id: '',
  service_type_id: '',
  is_connected: 'all',
  is_synchronized: 'all',
};

const INIT_STATE: TcSimsState = {
  tcSimsRouteParams: initialTcSimsRouteParams,
  tcSimsList: null,
  tcSimsListFetchStatus: FETCH_STATUS.NULL,
  simVerizonActivationStatusPostStatus: FETCH_STATUS.NULL,
  simVerizonActivation: null,
  tcSimDetails: null,
  tcSimDetailsFetchStatus: FETCH_STATUS.NULL,
  tcSimDetailsPostStatus: FETCH_STATUS.NULL,
  tcSimDetailsPatchStatus: FETCH_STATUS.NULL,
  tcSimDetailsDeleteStatus: FETCH_STATUS.NULL,
  tcSimDetailsStatusPatchStatus: FETCH_STATUS.NULL,
  tcSimStatusRefreshStatus: FETCH_STATUS.NULL,
  tcSimApplySuggestedDataPlanTypeStatus: FETCH_STATUS.NULL,
  updateCarrierDataPlanStatus: FETCH_STATUS.NULL,
  tcSimsBulkApplySuggestedDataPlanTypeStatus: FETCH_STATUS.NULL,
  tcSimsPostStatus: FETCH_STATUS.NULL,
  tcSimFixServiceStatus: FETCH_STATUS.NULL,
  tcSimContractPatchStatus: FETCH_STATUS.NULL,
  tcSimOwnershipTransferStatus: FETCH_STATUS.NULL,
  tcSimsBulk: null,
};

const tcSimsSlice = createSlice({
  name: 'tcSims',
  initialState: INIT_STATE,
  reducers: {
    setTcSimsRouteParams: (state, action) => {
      state.tcSimsRouteParams = { ...state.tcSimsRouteParams, ...action.payload };
    },
    setTcSimsList: (state, action) => {
      state.tcSimsList = action.payload;
    },
    setTcSimsListFetchStatus: (state, action) => {
      state.tcSimsListFetchStatus = action.payload;
    },
    setTcSimDetails: (state, action) => {
      state.tcSimDetails = action.payload;
    },
    setTcSimDetailsFetchStatus: (state, action) => {
      state.tcSimDetailsFetchStatus = action.payload;
    },
    setTcSimDetailsPostStatus: (state, action) => {
      state.tcSimDetailsPostStatus = action.payload;
    },
    setTcSimDetailsPatchStatus: (state, action) => {
      state.tcSimDetailsPatchStatus = action.payload;
    },
    setTcSimDetailsDeleteStatus: (state, action) => {
      state.tcSimDetailsDeleteStatus = action.payload;
    },
    setTcSimDetailsStatusPatchStatus: (state, action) => {
      state.tcSimDetailsStatusPatchStatus = action.payload;
    },
    setTcSimsPostStatus: (state, action) => {
      state.tcSimsPostStatus = action.payload;
    },
    setTcSimsBulk: (state, action) => {
      state.tcSimsBulk = action.payload;
    },
    setSimVerizonActivationStatusPostStatus: (state, action) => {
      state.simVerizonActivationStatusPostStatus = action.payload;
    },
    setSimVerizonActivation: (state, action) => {
      state.simVerizonActivation = action.payload;
    },
    setTcSimContractPatchStatus: (state, action) => {
      state.tcSimContractPatchStatus = action.payload;
    },
    setTcSimOwnershipTransferStatus: (state, action) => {
      state.tcSimOwnershipTransferStatus = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'sims') {
        state.tcSimsRouteParams = { ...state.tcSimsRouteParams, ...payload.data };
      }
    });

    // CREATE TC-SIMs DETAILS
    builder.addCase(postTcSims.pending, state => {
      state.tcSimsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTcSims.rejected, (state, action) => {
      state.tcSimsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTcSims.fulfilled, state => {
      state.tcSimsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIMs created successfully!');
      });
    });

    // CREATE TC-SIMs DETAILS
    builder.addCase(postTcSimDetails.pending, state => {
      state.tcSimDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTcSimDetails.rejected, (state, action) => {
      state.tcSimDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTcSimDetails.fulfilled, state => {
      state.tcSimDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM created successfully!');
      });
    });

    // GET TC-SIMs LIST
    builder.addCase(getTcSimsList.pending, state => {
      state.tcSimsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTcSimsList.rejected, (state, action) => {
      if (action.payload) {
        state.tcSimsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.tcSimsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getTcSimsList.fulfilled, (state, action) => {
      state.tcSimsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.tcSimsList = action.payload;
    });

    // GET TC-SIMs DETAILS
    builder.addCase(getTcSimDetails.pending, state => {
      state.tcSimDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getTcSimDetails.rejected, (state, action) => {
      state.tcSimDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.tcSimDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getTcSimDetails.fulfilled, (state, action) => {
      state.tcSimDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.tcSimDetails = action.payload;
    });

    // PATCH TC-SIMs DETAILS
    builder.addCase(patchTcSimDetails.pending, state => {
      state.tcSimDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchTcSimDetails.rejected, (state, action) => {
      state.tcSimDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchTcSimDetails.fulfilled, state => {
      state.tcSimDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM updated successfully!');
      });
    });

    // DELETE TC-SIMs DETAILS
    builder.addCase(deleteTcSimDetails.pending, state => {
      state.tcSimDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteTcSimDetails.rejected, (state, action) => {
      state.tcSimDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteTcSimDetails.fulfilled, state => {
      state.tcSimDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM deleted successfully!');
      });
    });

    // PATCH TC-SIM CONTRACT
    builder.addCase(patchTcSimContract.pending, state => {
      state.tcSimContractPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchTcSimContract.rejected, (state, action) => {
      state.tcSimContractPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchTcSimContract.fulfilled, state => {
      state.tcSimContractPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM contract updated successfully!');
      });
    });

    // TRANSFER SIM OWNERSHIP
    builder
      .addCase(postTcSimOwnershipTransfer.pending, state => {
        state.tcSimOwnershipTransferStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postTcSimOwnershipTransfer.rejected, (state, action) => {
        state.tcSimOwnershipTransferStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to transfer SIM ownership'));
      })
      .addCase(postTcSimOwnershipTransfer.fulfilled, state => {
        state.tcSimOwnershipTransferStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('SIM ownership transferred successfully!'));
      });

    // ACTIVATE TC-SIM
    builder.addCase(activateTcSim.pending, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(activateTcSim.rejected, (state, action) => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(activateTcSim.fulfilled, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.FULFILLED;
      snackbarUtils.success('SIM activation started. Please check back in a few minutes.');
    });

    // DEACTIVATE TC-SIM
    builder.addCase(deactivateTcSim.pending, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deactivateTcSim.rejected, (state, action) => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deactivateTcSim.fulfilled, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM deactivated successfully!');
      });
    });

    // DEACTIVATE TC-SIM
    builder.addCase(restoreTcSim.pending, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(restoreTcSim.rejected, (state, action) => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(restoreTcSim.fulfilled, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM restored successfully!');
      });
    });

    // SUSPEND TC-SIM
    builder.addCase(suspendTcSim.pending, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(suspendTcSim.rejected, (state, action) => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(suspendTcSim.fulfilled, state => {
      state.tcSimDetailsStatusPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('SIM suspended successfully!');
      });
    });

    // UPDATE SIM STATUS
    builder
      .addCase(refreshTcSimStatus.pending, state => {
        state.tcSimStatusRefreshStatus = FETCH_STATUS.PENDING;
      })
      .addCase(refreshTcSimStatus.rejected, (state, { payload }) => {
        state.tcSimStatusRefreshStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(payload);
      })
      .addCase(refreshTcSimStatus.fulfilled, state => {
        state.tcSimStatusRefreshStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.info('SIM status refresh started. Please check back in a few minutes.');
      });

    // SIM STATUS
    builder.addCase(postSimVerizonActivationStatus.pending, state => {
      state.simVerizonActivationStatusPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postSimVerizonActivationStatus.rejected, state => {
      state.simVerizonActivationStatusPostStatus = FETCH_STATUS.REJECTED;
    });
    builder.addCase(postSimVerizonActivationStatus.fulfilled, (state, action) => {
      state.simVerizonActivationStatusPostStatus = FETCH_STATUS.FULFILLED;
      state.simVerizonActivation = action.payload;
    });

    // APPLY SUGGESTED DATA PLAN TYPE
    builder
      .addCase(applySuggestedDataPlanType.pending, state => {
        state.tcSimApplySuggestedDataPlanTypeStatus = FETCH_STATUS.PENDING;
      })
      .addCase(applySuggestedDataPlanType.rejected, (state, action) => {
        state.tcSimApplySuggestedDataPlanTypeStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(applySuggestedDataPlanType.fulfilled, state => {
        state.tcSimApplySuggestedDataPlanTypeStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Suggested data plan type applied successfully!'));
      });

    // UPDATE CARRIER DATA PLAN TYPE
    builder
      .addCase(updateCarrierDataPlanType.pending, state => {
        state.updateCarrierDataPlanStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateCarrierDataPlanType.rejected, (state, action) => {
        state.updateCarrierDataPlanStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(updateCarrierDataPlanType.fulfilled, state => {
        state.updateCarrierDataPlanStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Data plan type applied successfully!'));
      });

    // BULK-APPLY SUGGESTED DATA PLAN TYPE
    builder
      .addCase(bulkApplySuggestedDataPlanType.pending, state => {
        state.tcSimsBulkApplySuggestedDataPlanTypeStatus = FETCH_STATUS.PENDING;
      })
      .addCase(bulkApplySuggestedDataPlanType.rejected, (state, action) => {
        state.tcSimsBulkApplySuggestedDataPlanTypeStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(bulkApplySuggestedDataPlanType.fulfilled, (state, action) => {
        state.tcSimsBulkApplySuggestedDataPlanTypeStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => {
          const attempted = action.payload.results.length;
          const succeeded = action.payload.results.filter(r => r).length;
          if (succeeded < attempted) {
            snackbarUtils.warning(`Applied ${succeeded} out of ${attempted} suggested data plan types!`);
          } else {
            snackbarUtils.success(`Successfully applied ${attempted} suggested data plan types!`);
          }
        });
      });

    // FIX SIM SERVICE
    builder
      .addCase(postTcSimFixService.pending, state => {
        state.tcSimFixServiceStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postTcSimFixService.rejected, (state, action) => {
        state.tcSimFixServiceStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to fix SIM service'));
      })
      .addCase(postTcSimFixService.fulfilled, state => {
        state.tcSimFixServiceStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('SIM service fixed successfully!'));
      });
  },
});

export const {
  setTcSimsRouteParams,
  setTcSimDetailsDeleteStatus,
  setTcSimsListFetchStatus,
  setTcSimDetailsFetchStatus,
  setTcSimDetailsPatchStatus,
  setTcSimDetailsPostStatus,
  setTcSimsList,
  setTcSimDetails,
  setTcSimsPostStatus,
  setTcSimsBulk,
  setSimVerizonActivationStatusPostStatus,
  setSimVerizonActivation,
  setTcSimContractPatchStatus,
  setTcSimOwnershipTransferStatus,
} = tcSimsSlice.actions;

export default tcSimsSlice.reducer;
